import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Form, Button, Table, Row, Col } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    getMaterials,
    createMaterial,
} from '../../../services/MaterialLogsService';
import {
    createRecipe,
    updateRecipe,
    addRecipeItem,
    deleteRecipeItem,
    getRecipe,
} from '../../../services/RecipesService';
import { convert, getPairedUnit, isConvertible, formatUnitForDisplay, getUnitPair } from '../../../utils/unitConversion';
import { getMaterialName } from '../../../utils/materialName';
import UnitSelector from '../Common/UnitSelector';
import { SALES_STATUSES } from '../Products/ProductStatus';

const DEFAULT_RECIPE_STATUS = 'rd'; // a new recipe is not in production yet

// Helper function to get default smaller unit for display
function getDefaultDisplayUnit(materialUnit) {
    if (!isConvertible(materialUnit)) {
        return materialUnit || 'unit';
    }
    const unitPair = getUnitPair(materialUnit);
    if (unitPair) {
        // Return the smaller unit (first in the pair: ['g', 'kg'] or ['ml', 'L'])
        return unitPair[0];
    }
    return materialUnit || 'unit';
}

const emptyMaterialForm = {
    material_name: '',
    name_nl: '',
    price: '',
    quantity_purchased: '1',
    unit: 'unit',
    price_includes_tax: true,
    tax_percentage: '',
    is_non_food: false,
    energy_for_purchase: '',
    protein_for_purchase: '',
    fat_for_purchase: '',
    carbohydrate_for_purchase: '',
};

function qtyPurchasedToGrams(qty, unit) {
    const q = parseFloat(qty) || 1;
    const u = (unit || 'g').toLowerCase();
    if (u === 'kg') return q * 1000;
    if (u === 'l') return q * 1000;
    if (u === 'ml') return q;
    return q;
}

export default function RecipeFormModal({ show, onHide, recipeId, onSaved }) {
    const { t, i18n } = useTranslation();
    const [nameNl, setNameNl] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [status, setStatus] = useState(DEFAULT_RECIPE_STATUS);
    const [yieldAmount, setYieldAmount] = useState('1');
    const [yieldUnit, setYieldUnit] = useState('pieces');
    const [items, setItems] = useState([]); // { material_id, material_name, quantity, unit, cost_excl, cost_incl } - for new rows we use material_id
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showAddMaterial, setShowAddMaterial] = useState(false);
    const [materialForm, setMaterialForm] = useState(emptyMaterialForm);
    const [forceUpdate, setForceUpdate] = useState(0); // Force re-render when units change

    const isEdit = !!recipeId;
    const dndKeyRef = useRef(0);
    const nextDndKey = () => String(++dndKeyRef.current);

    const loadMaterials = async () => {
        try {
            const res = await getMaterials();
            setMaterials(res.data || []);
        } catch {
            setMaterials([]);
        }
    };

    const loadRecipe = async () => {
        if (!recipeId) return;
        setLoading(true);
        try {
            const res = await getRecipe(recipeId);
            const r = res.data;
            setNameNl(r.name_nl || r.name || '');
            setNameEn(r.name_en || '');
            setStatus(SALES_STATUSES.includes(r.status) ? r.status : (r.is_rd ? 'rd' : 'draft'));
            setYieldAmount(String(r.yield_amount ?? 1));
            setYieldUnit(r.yield_unit || 'pieces');
            setItems((r.items || []).map((it) => {
                const materialUnit = it.unit || it.material_unit || 'unit';
                const defaultDisplayUnit = getDefaultDisplayUnit(materialUnit);
                return {
                    id: it.id,
                    material_id: it.material_id,
                    material_name: it.material_name,
                    material_name_nl: it.material_name_nl,
                    quantity: parseFloat(it.quantity) || 0,
                    unit: materialUnit,
                    displayUnit: defaultDisplayUnit,
                    cost_excl_tax: parseFloat(it.cost_excl_tax) || 0,
                    cost_incl_tax: parseFloat(it.cost_incl_tax ?? it.cost) || 0,
                    _dndKey: nextDndKey(),
                };
            }));
        } catch {
            toast.error(t('recipes.failedToLoad'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            loadMaterials();
            if (recipeId) {
                loadRecipe();
            } else {
                setNameNl('');
                setNameEn('');
                setStatus(DEFAULT_RECIPE_STATUS);
                setYieldAmount('1');
                setYieldUnit('pieces');
                setItems([]);
            }
        }
    }, [show, recipeId]);

    const totalExcl = items.reduce((s, it) => s + (it.cost_excl_tax || 0), 0);
    const totalIncl = items.reduce((s, it) => s + (it.cost_incl_tax || 0), 0);
    const yieldAmt = parseFloat(yieldAmount) || 1;
    const costForAmount = 1;
    const costForRatio = yieldAmt > 0 ? costForAmount / yieldAmt : 0;
    const costForExcl = Math.round(totalExcl * costForRatio * 100) / 100;
    const costForIncl = Math.round(totalIncl * costForRatio * 100) / 100;

    const getMaterialById = (id) => materials.find((m) => m.id === id);

    const addIngredientRow = (materialId, qty) => {
        const mat = getMaterialById(parseInt(materialId));
        if (!mat || !qty || qty <= 0) return;
        const qtyPurchased = parseFloat(mat.quantity_purchased) || 1;
        const priceExclPerUnit = (parseFloat(mat.price_excl_tax) || parseFloat(mat.price)) / qtyPurchased;
        const priceInclPerUnit = (parseFloat(mat.price_incl_tax) || parseFloat(mat.price)) / qtyPurchased;
        const materialUnit = mat.unit || 'unit';
        const defaultDisplayUnit = getDefaultDisplayUnit(materialUnit);
        setItems((prev) => [
            ...prev,
            {
                material_id: mat.id,
                material_name: mat.material_name,
                material_name_nl: mat.name_nl,
                quantity: parseFloat(qty),
                unit: materialUnit,
                displayUnit: defaultDisplayUnit,
                cost_excl_tax: Math.round(parseFloat(qty) * priceExclPerUnit * 100) / 100,
                cost_incl_tax: Math.round(parseFloat(qty) * priceInclPerUnit * 100) / 100,
                _dndKey: nextDndKey(),
            },
        ]);
    };

    const removeIngredient = (idx) => {
        setItems((prev) => prev.filter((_, i) => i !== idx));
    };

    const updateIngredientQuantity = (idx, newQuantityStr) => {
        const displayQty = parseFloat(newQuantityStr);
        if (isNaN(displayQty) || displayQty <= 0) return;

        setItems((prev) => {
            const updated = [...prev];
            const item = updated[idx];
            if (!item) return prev;

            const mat = getMaterialById(item.material_id);
            if (!mat) return prev;

            const materialUnit = item.unit || mat.unit || 'unit';
            const displayUnit = item.displayUnit ?? materialUnit;
            const qtyInMaterialUnit = isConvertible(materialUnit)
                ? convert(displayQty, displayUnit, materialUnit)
                : displayQty;
            if (qtyInMaterialUnit <= 0) return prev;

            const qtyPurchased = parseFloat(mat.quantity_purchased) || 1;
            const priceExclPerUnit = (parseFloat(mat.price_excl_tax) || parseFloat(mat.price)) / qtyPurchased;
            const priceInclPerUnit = (parseFloat(mat.price_incl_tax) || parseFloat(mat.price)) / qtyPurchased;

            updated[idx] = {
                ...item,
                quantity: qtyInMaterialUnit,
                cost_excl_tax: Math.round(qtyInMaterialUnit * priceExclPerUnit * 100) / 100,
                cost_incl_tax: Math.round(qtyInMaterialUnit * priceInclPerUnit * 100) / 100,
            };
            return updated;
        });
    };

    const updateIngredientDisplayUnit = (idx, newDisplayUnit) => {
        setItems((prev) => {
            const updated = prev.map((item, i) => {
                if (i === idx) {
                    const materialUnit = item.unit || 'unit';
                    const currentDisplayUnit = item.displayUnit ?? materialUnit;
                    
                    // Only update if the unit actually changed
                    const currentNorm = (currentDisplayUnit || '').trim().toLowerCase();
                    const newNorm = (newDisplayUnit || '').trim().toLowerCase();
                    if (currentNorm === newNorm) {
                        return item; // No change needed
                    }
                    
                    // Create a completely new object to ensure React detects the change
                    return { 
                        ...item, 
                        displayUnit: newDisplayUnit 
                    };
                }
                return item;
            });
            // Return a new array to ensure React detects the change
            return [...updated];
        });
        // Force a re-render to ensure the conversion is displayed
        setForceUpdate(prev => prev + 1);
    };

    const handleAddMaterial = async (e) => {
        e.preventDefault();
        if (!materialForm.material_name.trim() || !materialForm.price) {
            toast.warning(`${t('materials.materialName')} / ${t('common.price')} ${t('common.required').toLowerCase()}`);
            return;
        }
        if (!materialForm.name_nl.trim()) {
            toast.warning(t('materials.nameDutchWarning'));
            return;
        }
        if (!materialForm.is_non_food) {
            const energy = parseFloat(materialForm.energy_for_purchase);
            const protein = parseFloat(materialForm.protein_for_purchase);
            const fat = parseFloat(materialForm.fat_for_purchase);
            const carb = parseFloat(materialForm.carbohydrate_for_purchase);
            if ([energy, protein, fat, carb].some(v => isNaN(v) || v < 0)) {
                toast.warning(t('materials.errors.requiredNutrition'));
                return;
            }
        }
        try {
            setSubmitting(true);
            const payload = {
                material_name: materialForm.material_name.trim(),
                name_nl: materialForm.name_nl.trim(),
                price: parseFloat(materialForm.price),
                quantity_purchased: parseFloat(materialForm.quantity_purchased) || 1,
                unit: materialForm.unit.trim() || 'unit',
                price_includes_tax: materialForm.price_includes_tax,
                tax_percentage: materialForm.tax_percentage ? parseFloat(materialForm.tax_percentage) : null,
                is_non_food: materialForm.is_non_food,
                source: null,
                location: null,
                notes: null,
            };
            if (!materialForm.is_non_food) {
                const grams = qtyPurchasedToGrams(materialForm.quantity_purchased, materialForm.unit);
                const factor = grams / 100;
                if (factor <= 0) {
                    toast.warning(t('materials.errors.quantityRequired'));
                    setSubmitting(false);
                    return;
                }
                payload.energy_per_100g = Math.round((parseFloat(materialForm.energy_for_purchase) / factor) * 100) / 100;
                payload.protein_per_100g = Math.round((parseFloat(materialForm.protein_for_purchase) / factor) * 100) / 100;
                payload.fat_per_100g = Math.round((parseFloat(materialForm.fat_for_purchase) / factor) * 100) / 100;
                payload.carbohydrate_per_100g = Math.round((parseFloat(materialForm.carbohydrate_for_purchase) / factor) * 100) / 100;
            }
            await createMaterial(payload);
            toast.success(t('materials.success.added'));
            setShowAddMaterial(false);
            setMaterialForm(emptyMaterialForm);
            await loadMaterials();
        } catch (err) {
            toast.error(err.response?.data?.error || t('materials.errors.save'));
        } finally {
            setSubmitting(false);
        }
    };

    const onDragEnd = useCallback((result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        setItems((prev) => {
            const next = Array.from(prev);
            const [moved] = next.splice(result.source.index, 1);
            next.splice(result.destination.index, 0, moved);
            return next;
        });
    }, []);

    const handleSave = async () => {
        if (!nameNl.trim()) {
            toast.warning(t('recipes.recipeNameRequired'));
            return;
        }
        if (items.length === 0) {
            toast.warning(t('recipes.addAtLeastOneIngredient'));
            return;
        }
        setSubmitting(true);
        try {
            let id = recipeId;
            if (recipeId) {
                await updateRecipe(recipeId, {
                    name_nl: nameNl.trim(),
                    name_en: nameEn.trim(),
                    status,
                    yield_amount: parseFloat(yieldAmount) || 1,
                    yield_unit: yieldUnit.trim() || 'pieces',
                });
                const res = await getRecipe(recipeId);
                const existingIds = (res.data.items || []).map((it) => it.id);
                for (const itemId of existingIds) {
                    await deleteRecipeItem(recipeId, itemId);
                }
            } else {
                const res = await createRecipe({
                    name_nl: nameNl.trim(),
                    name_en: nameEn.trim(),
                    status,
                    yield_amount: parseFloat(yieldAmount) || 1,
                    yield_unit: yieldUnit.trim() || 'pieces',
                });
                id = res.data.id;
            }
            for (let i = 0; i < items.length; i++) {
                await addRecipeItem(id, items[i].material_id, items[i].quantity, i);
            }
            toast.success(isEdit ? t('recipes.recipeUpdated') : t('recipes.recipeSaved'));
            onSaved?.();
            onHide();
        } catch (err) {
            toast.error(err.response?.data?.error || t('recipes.failedToSave'));
        } finally {
            setSubmitting(false);
        }
    };

    const [newMatId, setNewMatId] = useState('');
    const [newQty, setNewQty] = useState('');

    const materialOptions = useMemo(
        () =>
            materials.map((m) => ({
                value: m.id,
                label: `${getMaterialName(m)} (${(m.price_per_unit ?? m.price ?? 0).toFixed(2)}/${formatUnitForDisplay(m.unit)})`,
            })),
        [materials, i18n.resolvedLanguage]
    );
    const materialSelectValue = useMemo(
        () => materialOptions.find((o) => o.value === parseInt(newMatId, 10)) || null,
        [materialOptions, newMatId]
    );
    const filterMaterialOption = (option, rawInput) => {
        const q = (rawInput || '').trim().toLowerCase();
        if (!q) return true;
        const tokens = q.split(/\s+/).filter(Boolean);
        const label = (option.label || '').toLowerCase();
        return tokens.every((t) => label.includes(t));
    };

    const handleAddRow = () => {
        if (!newMatId || !newQty || parseFloat(newQty) <= 0) {
            toast.warning(t('recipes.selectMaterialAndQuantity'));
            return;
        }
        addIngredientRow(newMatId, newQty);
        setNewMatId('');
        setNewQty('');
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? t('recipes.editRecipe') : t('recipes.addRecipe')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('common.loading')}</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t('recipes.recipeNameNl')}</Form.Label>
                                    <Form.Control
                                        placeholder={t('recipes.recipeNameNlPlaceholder')}
                                        value={nameNl}
                                        onChange={(e) => setNameNl(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>{t('recipes.recipeNameEn')}</Form.Label>
                                    <Form.Control
                                        placeholder={t('recipes.recipeNameEnPlaceholder')}
                                        value={nameEn}
                                        onChange={(e) => setNameEn(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>{t('recipes.makes')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        placeholder="e.g. 10"
                                        value={yieldAmount}
                                        onChange={(e) => setYieldAmount(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>{t('common.unit')}</Form.Label>
                                    <Form.Control
                                        placeholder={t('common.unit')}
                                        value={yieldUnit}
                                        onChange={(e) => setYieldUnit(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col>
                                <Form.Label htmlFor="recipe_status">{t('recipes.statusLabel')}</Form.Label>
                                <Form.Select id="recipe_status" value={status} onChange={(e) => setStatus(e.target.value)}>
                                    {SALES_STATUSES.map((st) => (
                                        <option key={st} value={st}>{t(`products.status.${st}`)}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">{t('recipes.statusHelp')}</Form.Text>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <h6 className="mb-2">{t('recipes.ingredients')}</h6>
                            <Row className="mb-3">
                                <Col md={5}>
                                    <Select
                                        classNamePrefix="react-select"
                                        placeholder={t('recipes.selectMaterial')}
                                        isClearable
                                        isSearchable
                                        options={materialOptions}
                                        value={materialSelectValue}
                                        onChange={(opt) => setNewMatId(opt?.value ? String(opt.value) : '')}
                                        filterOption={filterMaterialOption}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        placeholder="Qty"
                                        value={newQty}
                                        onChange={(e) => setNewQty(e.target.value)}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Button variant="primary" onClick={handleAddRow}>
                                        <i className="fa-solid fa-plus me-1" /> {t('common.add')}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ms-2"
                                        onClick={() => setShowAddMaterial(true)}
                                    >
                                        + {t('recipes.newMaterial')}
                                    </Button>
                                </Col>
                            </Row>

                            <DragDropContext onDragEnd={onDragEnd}>
                                <Table responsive bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 32 }}></th>
                                            <th>{t('materials.columns.material')}</th>
                                            <th>{t('common.quantity')}</th>
                                            <th>{t('common.unit')}</th>
                                            <th>{t('recipes.priceExc')}</th>
                                            <th>{t('recipes.priceInc')}</th>
                                            <th style={{ width: 50 }}></th>
                                        </tr>
                                    </thead>
                                    <Droppable droppableId="ingredients">
                                        {(provided) => (
                                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                {items.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center text-muted py-4">
                                                            {t('recipes.noIngredients')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    items.map((it, idx) => {
                                                        const materialUnit = it.unit || 'unit';
                                                        const displayUnit = it.displayUnit ?? materialUnit;
                                                        let displayedQty = parseFloat(it.quantity) || 0;
                                                        const materialUnitNorm = (materialUnit || '').trim().toLowerCase();
                                                        const displayUnitNorm = (displayUnit || '').trim().toLowerCase();
                                                        if (isConvertible(materialUnit) && isConvertible(displayUnit) && materialUnitNorm !== displayUnitNorm) {
                                                            displayedQty = convert(it.quantity, materialUnit, displayUnit);
                                                        }
                                                        const dndKey = it._dndKey || String(idx);
                                                        return (
                                                            <Draggable key={dndKey} draggableId={dndKey} index={idx}>
                                                                {(provided, snapshot) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            background: snapshot.isDragging ? '#f0f4ff' : undefined,
                                                                        }}
                                                                    >
                                                                        <td
                                                                            {...provided.dragHandleProps}
                                                                            style={{ textAlign: 'center', cursor: 'grab', color: '#adb5bd', verticalAlign: 'middle' }}
                                                                        >
                                                                            <i className="fa-solid fa-grip-vertical" />
                                                                        </td>
                                                                        <td>{getMaterialName(it)}</td>
                                                                        <td>
                                                                            <Form.Control
                                                                                type="number"
                                                                                step="0.01"
                                                                                min="0.01"
                                                                                value={displayedQty}
                                                                                onChange={(e) => updateIngredientQuantity(idx, e.target.value)}
                                                                                style={{ width: '100px' }}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            {isConvertible(materialUnit) ? (
                                                                                <UnitSelector
                                                                                    key={`unit-selector-${idx}-${displayUnit}-${forceUpdate}`}
                                                                                    unit={displayUnit}
                                                                                    onUnitChange={(newUnit) => updateIngredientDisplayUnit(idx, newUnit)}
                                                                                    quantity={displayedQty}
                                                                                    onQuantityChange={() => {}}
                                                                                    materialUnit={materialUnit}
                                                                                />
                                                                            ) : (
                                                                                <span>{formatUnitForDisplay(materialUnit)}</span>
                                                                            )}
                                                                        </td>
                                                                        <td>{it.cost_excl_tax.toFixed(2)}</td>
                                                                        <td>{it.cost_incl_tax.toFixed(2)}</td>
                                                                        <td>
                                                                            <Button
                                                                                variant="light"
                                                                                size="sm"
                                                                                className="text-danger p-1"
                                                                                onClick={() => removeIngredient(idx)}
                                                                            >
                                                                                <i className="fa-solid fa-trash" />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })
                                                )}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                </Table>
                            </DragDropContext>
                        </div>

                        {items.length > 0 && (
                            <div className="p-3 bg-light rounded">
                                <Row>
                                    <Col md={6}>
                                        <h6 className="mb-1">{t('recipes.total')} ({yieldAmount} {(yieldUnit && !['1', 'unit'].includes(String(yieldUnit).toLowerCase())) ? yieldUnit : 'pieces'})</h6>
                                        <p className="mb-0">
                                            <strong>{t('recipes.priceExc')}:</strong> {totalExcl.toFixed(2)} &nbsp;
                                            <strong>{t('recipes.priceInc')}:</strong> {totalIncl.toFixed(2)}
                                        </p>
                                    </Col>
                                    <Col md={6}>
                                        <h6 className="mb-1">{t('recipes.costPerUnit', { unit: (yieldUnit && !['1', 'unit'].includes(String(yieldUnit).toLowerCase())) ? yieldUnit : 'pieces' })}</h6>
                                        <p className="mb-0 fw-bold">
                                            (exc) {costForExcl.toFixed(2)} / (inc) {costForIncl.toFixed(2)}
                                        </p>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={submitting || loading}>
                    {submitting ? t('common.saving') : t('recipes.save')}
                </Button>
            </Modal.Footer>

            <Modal show={showAddMaterial} onHide={() => setShowAddMaterial(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('materials.addMaterial')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddMaterial}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label>{t('materials.materialNameEN')} *</Form.Label>
                                    <Form.Control
                                        value={materialForm.material_name}
                                        onChange={(e) => setMaterialForm((p) => ({ ...p, material_name: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label>{t('materials.materialNameNL')} *</Form.Label>
                                    <Form.Control
                                        value={materialForm.name_nl}
                                        onChange={(e) => setMaterialForm((p) => ({ ...p, name_nl: e.target.value }))}
                                        placeholder={t('materials.materialNameNLPlaceholder')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-2">
                                    <Form.Label>{t('common.price')} *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={materialForm.price}
                                        onChange={(e) => setMaterialForm((p) => ({ ...p, price: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-2">
                                    <Form.Label>{t('materials.columns.qtyUnit')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={materialForm.quantity_purchased}
                                        onChange={(e) => setMaterialForm((p) => ({ ...p, quantity_purchased: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label>{t('common.unit')}</Form.Label>
                                    <Form.Control
                                        value={materialForm.unit}
                                        onChange={(e) => setMaterialForm((p) => ({ ...p, unit: e.target.value }))}
                                        placeholder={t('common.unit')}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group className="mb-2">
                                    <Form.Check
                                        type="switch"
                                        id="is_non_food"
                                        label={t('materials.nonFoodDescription')}
                                        checked={materialForm.is_non_food}
                                        onChange={(e) => setMaterialForm((p) => {
                                            const next = { ...p, is_non_food: e.target.checked };
                                            if (e.target.checked) {
                                                next.energy_for_purchase = next.protein_for_purchase = next.fat_for_purchase = next.carbohydrate_for_purchase = '';
                                            }
                                            return next;
                                        })}
                                    />
                                </Form.Group>
                            </Col>
                            {!materialForm.is_non_food && (
                                <>
                                    <Col md={12}>
                                        <small className="text-muted d-block mb-2">{t('materials.nutritionNote')}</small>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>{t('materials.energy')} *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={materialForm.energy_for_purchase}
                                                onChange={(e) => setMaterialForm((p) => ({ ...p, energy_for_purchase: e.target.value }))}
                                                placeholder="e.g. 12500"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>{t('materials.protein')} *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={materialForm.protein_for_purchase}
                                                onChange={(e) => setMaterialForm((p) => ({ ...p, protein_for_purchase: e.target.value }))}
                                                placeholder="e.g. 400"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>{t('materials.fat')} *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={materialForm.fat_for_purchase}
                                                onChange={(e) => setMaterialForm((p) => ({ ...p, fat_for_purchase: e.target.value }))}
                                                placeholder="e.g. 100"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>{t('materials.carbohydrate')} *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={materialForm.carbohydrate_for_purchase}
                                                onChange={(e) => setMaterialForm((p) => ({ ...p, carbohydrate_for_purchase: e.target.value }))}
                                                placeholder="e.g. 2500"
                                            />
                                        </Form.Group>
                                    </Col>
                                </>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddMaterial(false)}>{t('common.cancel')}</Button>
                        <Button variant="primary" type="submit" disabled={submitting}>{t('common.add')}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Modal>
    );
}
